<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { t } from "@/plugins/i18n";
import { buildRailGroups, groupsToExpand } from "./railGroups";
import DimensionDO = App.Data.DimensionDO;

const {
	dimensions,
	activePins = [],
	selectedNameClean = null,
	allowValueSearch = false,
	domainOptions = [],
	isDomainSelectable = false,
} = defineProps<{
	dimensions: DimensionDO[];
	activePins?: string[];
	selectedNameClean?: string | null;
	allowValueSearch?: boolean;
	domainOptions?: string[];
	isDomainSelectable?: boolean;
}>();

const emit = defineEmits<{
	(event: "select", dimension: DimensionDO): void;
	(event: "search-by-value"): void;
}>();

const domain = defineModel<string | null>("domain", { default: null });

const search = ref("");
const manuallyToggled = ref<Set<string>>(new Set());

const query = computed(() => search.value ?? "");

const groups = computed(() =>
	buildRailGroups(dimensions, {
		pins: activePins,
		query: query.value,
		t,
	})
);

const autoExpanded = computed(() =>
	groupsToExpand(groups.value, {
		query: query.value,
		selectedNameClean,
	})
);

watch(query, () => {
	manuallyToggled.value = new Set();
});

const isOpen = (key: string) => {
	const auto = autoExpanded.value.includes(key);

	return manuallyToggled.value.has(key) ? !auto : auto;
};

const toggle = (key: string) => {
	const next = new Set(manuallyToggled.value);

	if (next.has(key)) {
		next.delete(key);
	} else {
		next.add(key);
	}

	manuallyToggled.value = next;
};

const select = (dimension: DimensionDO) => {
	manuallyToggled.value = new Set();
	search.value = "";

	emit("select", dimension);
};

const selectFirst = () => {
	if (search.value === "") {
		return;
	}

	const first = groups?.value.find((group) => group.items.length > 0)?.items[0];

	if (first) {
		select(first.dimension);
	}
};
</script>
<template>
	<div class="dimension-rail">
		<div
			v-if="isDomainSelectable && domainOptions.length > 1"
			class="pa-2 pb-0"
		>
			<v-select
				v-model="domain"
				:items="domainOptions"
				:item-title="(option) => t(`property_domains.${option}.title`)"
				:item-value="(option) => option"
				:placeholder="t('filterDialog.rail.allDomains')"
				prepend-inner-icon="fa: fa-regular fa-layer-group"
				clearable
				hide-details
				density="compact"
				bg-color="secondary"
			/>
		</div>
		<div class="pa-2">
			<v-text-field
				v-model="search"
				:placeholder="t('filterDialog.rail.search')"
				prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
				hide-details
				clearable
				density="compact"
				autocomplete="off"
				@keydown.enter="selectFirst"
			/>
		</div>
		<div class="groups">
			<div
				v-if="groups.length === 0"
				class="pa-4 text-caption text-center muted"
			>
				{{ t("filterDialog.rail.empty", { query }) }}
			</div>
			<div v-for="group in groups" :key="group.key" class="group">
				<button
					type="button"
					class="group-head"
					:aria-expanded="isOpen(group.key)"
					@click="toggle(group.key)"
				>
					<v-icon
						:icon="
							isOpen(group.key)
								? 'fa: fa-regular fa-chevron-down'
								: 'fa: fa-regular fa-chevron-right'
						"
						size="12"
					/>
					<v-icon
						v-if="group.isPinned"
						icon="fa: fa-solid fa-thumbtack"
						size="12"
						color="warning"
					/>
					<v-icon
						v-else
						:icon="`fa: fa-regular ${t(`property_families.${group.key}.icon`)}`"
						size="12"
					/>
					<span class="group-label text-caption">
						{{ group.isPinned ? t("filterDialog.rail.pinned") : group.label }}
					</span>
					<span class="group-count text-caption">{{ group.items.length }}</span>
				</button>
				<div v-if="isOpen(group.key)" class="group-items">
					<button
						v-for="item in group.items"
						:key="`${group.key}-${item.dimension.name_clean}`"
						type="button"
						class="item text-caption"
						:class="{
							selected: item.dimension.name_clean === selectedNameClean,
						}"
						@click="select(item.dimension)"
					>
						<span class="item-label">{{ item.title }}</span>
						<v-icon
							v-if="item.isPinned && !group.isPinned"
							icon="fa: fa-solid fa-thumbtack"
							size="12"
							color="warning"
						/>
					</button>
				</div>
			</div>
		</div>
		<template v-if="allowValueSearch">
			<v-divider />
			<div class="pa-2">
				<v-btn
					variant="text"
					size="small"
					density="comfortable"
					class="by-value"
					@click="emit('search-by-value')"
					v-tooltip="t('filterDialog.rail.byValueHint')"
					prepend-icon="fa: fa-regular fa-magnifying-glass"
				>
					{{ t("filterDialog.rail.byValue") }}
				</v-btn>
			</div>
		</template>
	</div>
</template>
<style scoped lang="scss">
.dimension-rail {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	background: rgb(var(--color-background));
	border-right: 1px solid rgba(var(--color-on-background), 0.2);
	scrollbar-color: rgb(var(--color-secondary)) transparent;
	scrollbar-width: thin;
}

.groups {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	scrollbar-width: thin;
}

.group-head {
	display: grid;
	grid-template-columns: 0.75rem auto 1fr auto;
	align-items: center;
	gap: 0.4rem;
	width: 100%;
	padding: 0.4rem 0.6rem;
	text-align: left;
	cursor: pointer;

	&:hover {
		background: rgb(var(--color-secondary));
	}
}

.group-label {
	font-weight: 600;
	color: rgb(var(--color-on-surface));
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.group-count,
.muted {
	color: rgb(var(--color-on-background));
}

.group-items {
	display: flex;
	flex-direction: column;
}

.item {
	display: flex;
	align-items: center;
	gap: 0.4rem;
	padding: 0.35rem 0.6rem 0.35rem 1.7rem;
	text-align: left;
	cursor: pointer;

	&:hover {
		background: rgb(var(--color-secondary));
	}

	&.selected {
		background: rgb(var(--color-secondary));
		font-weight: 600;
		box-shadow: inset 2px 0 0 rgb(var(--color-primary));
	}
}

.item-label {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.by-value {
	text-transform: none;
}
</style>
